import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectStep {
  key: string;
  title: string;
  /** Kort mening som förklarar varför steget behövs */
  why: string;
  docNumber?: string;
  done: boolean;
  /** Vilken grupp i verktygsblocket steget hör till */
  phase: "basp" | "basu";
  /** Relativ väg att navigera till */
  path: string;
}

interface Result {
  steps: ProjectStep[];
  nextStep: ProjectStep | null;
  doneCount: number;
  total: number;
  loading: boolean;
  refresh: () => void;
}

/**
 * Räknar ut projektets status för de obligatoriska dokumenten och
 * pekar ut ett enda nästa steg för användaren.
 */
export function useProjectNextStep(projectId: string): Result {
  const [steps, setSteps] = useState<ProjectStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const count = async (
        table: "work_area_surveys" | "work_environment_plans" | "safety_rule_documents" | "work_preparations",
      ) => {
        const { count: c } = await supabase
          .from(table)
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId);
        return c ?? 0;
      };

      const countRisk = async (phase: string) => {
        const { count: c } = await supabase
          .from("folder_risk_assessments")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId)
          .eq("phase", phase);
        return c ?? 0;
      };

      const [area, amp, rules, prep, riskBygg, riskBruk] = await Promise.all([
        count("work_area_surveys"),
        count("work_environment_plans"),
        count("safety_rule_documents"),
        count("work_preparations"),
        countRisk("byggskede"),
        countRisk("bruksskede"),
      ]);

      if (cancelled) return;

      setSteps([
        {
          key: "arbetsomrade",
          title: "Utred arbetsområdet",
          why: "Grunden – visar vilka förutsättningar och risker platsen har.",
          docNumber: "Dok 10",
          done: area > 0,
          phase: "basp",
          path: `/arbetsomrade/${projectId}`,
        },
        {
          key: "risk-bygg",
          title: "Sammanställ risker under byggtiden",
          why: "Krävs för att kunna planera skyddsåtgärder innan arbetet startar.",
          docNumber: "Dok 30b",
          done: riskBygg > 0,
          phase: "basp",
          path: "",
        },
        {
          key: "amp",
          title: "Skapa arbetsmiljöplanen",
          why: "Huvuddokumentet som samlar planen för hela projektet.",
          docNumber: "Dok 12",
          done: amp > 0,
          phase: "basp",
          path: "",
        },
        {
          key: "regler",
          title: "Sätt regler på arbetsplatsen",
          why: "Alla på plats behöver veta vad som gäller.",
          docNumber: "Dok 15",
          done: rules > 0,
          phase: "basp",
          path: `/skyddsregler/${projectId}`,
        },
        {
          key: "risk-bruk",
          title: "Sammanställ kvarstående risker",
          why: "Risker som finns kvar när byggnaden används och underhålls.",
          docNumber: "Dok 31",
          done: riskBruk > 0,
          phase: "basp",
          path: "",
        },
        {
          key: "arbetsberedning",
          title: "Gör en arbetsberedning",
          why: "Beskriver hur ett riskfyllt moment ska utföras säkert.",
          docNumber: "Dok 24",
          done: prep > 0,
          phase: "basu",
          path: "",
        },
      ]);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, tick]);

  const doneCount = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done) ?? null;

  return { steps, nextStep, doneCount, total: steps.length, loading, refresh };
}
